import React, {useEffect, useState} from "react";
import EnterpriseComponent from "../../eoec/ec/EnterpriseComponent";
import {makeEnterpriseComponent} from "../../util/utility";
import {prepareBuffer} from "../../util/Buffer";
import EventBus from "../../util/EventBus";
import {SessionContext} from "../../util/SessionContext";
import {Avatar, Button, IconButton, Tooltip, Typography} from "@mui/material";
import {Blue3, Grey3, Grey5, Grey6, Black} from "../../theme/styles";
import {ControlPoint, Edit} from "@mui/icons-material";
import spinner from "../../assets/Spinner-1s-200px.svg";
import css_buttons from "../Common/css/CCButton.module.scss";
import SecondaryPositionFormApplet from "../../eoec/applet/impl/ProjectPlan/SecondaryPositionFormApplet";
import SecondaryUserGroupFormApplet from "../../eoec/applet/impl/ProjectPlan/SecondaryUserGroupFormApplet";
import SecondaryWorkGroupFormApplet from "../../eoec/applet/impl/ProjectPlan/SecondaryWorkGroupFormApplet";
import PrimaryPositionFormApplet from "../../eoec/applet/impl/ProjectPlan/PrimaryPositionFormApplet";
import PrimaryUserGroupFormApplet from "../../eoec/applet/impl/ProjectPlan/PrimaryUserGroupFormApplet";
import PrimaryWorkGroupFormApplet from "../../eoec/applet/impl/ProjectPlan/PrimaryWorkGroupFormApplet";
import ShareAccessLevel from "./ShareAccessLevel";

// Visibility tab for a first-class object revision. Ported from t5's TermVisibilityTab, which was
// itself ported from this repo's ProjectPlanVisibilityTab -- so the form applets it opens are the
// Project Plan ones, reused unchanged. They bind by ATTRIBUTE NAME (PrimaryPositionUSID, PositionUSID)
// and those names are identical on the ObjectRevision and membership ECs, which is why no Foundation
// copies are needed.
//
// Three sections -- Positions, User Groups, Work Groups -- each with:
//   * the PRIMARY owner, stored on the revision itself and edited through the Primary* dialogs;
//   * the SHARES, flat lists of memberships keyed by SourceType "ObjectRevision" and SourceUSID = the
//     revision USID, added through the Secondary* dialogs and removed with Disassociate.
//
// Position and User Group shares can additionally carry per-share CAPABILITY grants (the Capabilities
// button); Work Group shares cannot, matching t5.
interface Props {
    objectRevisionUSID: string;
    sessionContext: SessionContext;
    // Raised after a Primary is changed, so the page's own copy of the record does not go stale.
    onPrimaryChanged?: () => void;
}

const SOURCE_TYPE = "ObjectRevision";

/** Who granted this share, in words -- or null for one somebody made deliberately.
 *
 *  NULL IS THE INTERESTING CASE and must stay unlabelled: a deliberate share is the normal thing,
 *  and annotating it would make the engine's rows look like the default rather than the exception.
 */
function grantedBy(member: Map<string, any>): string | null {
    if (member.get("GrantedByApprovalRequestUSID")) return "Shared for an approval";
    if (member.get("GrantedByTrainingCurriculumUSID")) return "Shared for training";
    if (member.get("GrantedByFolderUSID")) return "Shared by its folder";
    return null;
}

// Stamped in the state initializer -- BEFORE the ECs are queried or written, so a share is never
// stored under the wrong type.
function withSourceType(ec: any) {
    ec?.setSourceType?.(SOURCE_TYPE);
    return ec;
}

export default function ObjectVisibilityTab(props: Props) {
    const {objectRevisionUSID, sessionContext} = props;

    const [showEditPrimaryPosition, setShowEditPrimaryPosition] = useState<boolean>(false);
    const [showEditPrimaryUserGroup, setShowEditPrimaryUserGroup] = useState<boolean>(false);
    const [showEditPrimaryWorkGroup, setShowEditPrimaryWorkGroup] = useState<boolean>(false);

    // The revision itself. Loaded here rather than handed down, because the Primary dialogs operate on
    // a live EC (they write through it), and the page holds only a plain JSON copy of the record.
    const [ObjectRevisionEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("ObjectRevision"));
    const [revisionBuffer, setRevisionBuffer] = useState<Map<string, any> | null>(null);

    // Visibility is editable while the record is live, and FROZEN once it is SUPERSEDED, CANCELLED or
    // VOID. DEPRECATED is deliberately absent: it still resolves, so its visibility stays editable.
    const status = revisionBuffer ? revisionBuffer.get("Status") : null;
    const frozen = status === "SUPERSEDED" || status === "CANCELLED" || status === "VOID";

    const account: any = sessionContext?.getAccount?.();
    const primaryName = (companyKey: string, usid: any) => {
        if (!usid) return null;
        // Prefer the (already-loaded) account list of the user's OWN memberships; fall back to the
        // realm-wide lookup map so a Primary set to an entity the current user is not a member of
        // still resolves to a name.
        const list = account ? account[companyKey] : null;
        const fromCompany = Array.isArray(list) ? list.find((e: any) => e.USID === usid)?.Name : null;
        if (fromCompany) return fromCompany;
        const nameMap = companyKey === "Positions" ? positionNames : companyKey === "UserGroups" ? userGroupNames : workGroupNames;
        return nameMap.get(usid) ?? null;
    };

    const [PositionMembershipEnterpriseComponent] = useState<EnterpriseComponent>(() => withSourceType(makeEnterpriseComponent("PositionMembership")));
    const [UserGroupMembershipEnterpriseComponent] = useState<EnterpriseComponent>(() => withSourceType(makeEnterpriseComponent("UserGroupMembership")));
    const [WorkGroupMembershipEnterpriseComponent] = useState<EnterpriseComponent>(() => withSourceType(makeEnterpriseComponent("WorkGroupMembership")));

    // Read-only lookup ECs used only to resolve a stored Primary*USID to a display name. The Primary
    // picker lets a steward choose ANY account entity, not just ones the current user belongs to, so
    // the user's own memberships are not sufficient -- see primaryName() above.
    const [PositionEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("Position"));
    // The PRIMARY position, loaded on its own with v1.Position-002 so it arrives with the account
    // users holding it. The realm-wide lookup above uses -001 and answers only "what is this
    // position called"; nothing here previously fetched the primary's membership, which is why it
    // could only ever be rendered as a line of text.
    const [PrimaryPositionEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("Position"));
    const [PrimaryUserGroupEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("UserGroup"));
    const [PrimaryWorkGroupEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("WorkGroup"));
    const [primaryPosition, setPrimaryPosition] = useState<Map<string, any> | null>(null);
    const [primaryUserGroup, setPrimaryUserGroup] = useState<Map<string, any> | null>(null);
    const [primaryWorkGroup, setPrimaryWorkGroup] = useState<Map<string, any> | null>(null);

    // WHO MAY REASSIGN A PRIMARY OWNER. Changing it moves the record to another position or group,
    // which is a decision for whoever answers for it -- not for anybody who can open the tab.
    //
    //  * Positions: the primary account user OF THE PRIMARY POSITION. That person is the record's
    //    owner in the sense that matters.
    //  * User Groups and Work Groups: that same person, OR the primary account user of the group
    //    itself where one is set -- a group's own owner may hand their group's slot on.
    //
    // UNSET IS OPEN. With no primary owner there is no primary account user, so gating on one would
    // leave a record nobody could ever assign -- the tab would show a pencil to no one. A record
    // with no owner is assigned by whoever gets there; from then on the rules above apply.
    //
    // THIS HIDES, IT DOES NOT ENFORCE. The server has to refuse the write too; until it does, this
    // is UX rather than a control. See the note on the write path.
    const me = sessionContext?.getAccountUserUSID?.() ?? null;
    const positionOwner = primaryPosition?.get("PrimaryAccountUserUSID") ?? null;

    const mayEditPrimary = (ownerPrimaryAccountUserUSID: any, ownerSet: boolean) => {
        if (frozen) return false;
        if (!ownerSet) return true;
        if (!me) return false;
        if (positionOwner && String(positionOwner) === String(me)) return true;
        return !!ownerPrimaryAccountUserUSID && String(ownerPrimaryAccountUserUSID) === String(me);
    };
    const [UserGroupEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("UserGroup"));
    const [WorkGroupEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("WorkGroup"));
    const [positionNames, setPositionNames] = useState<Map<string, string>>(new Map());
    const [userGroupNames, setUserGroupNames] = useState<Map<string, string>>(new Map());
    const [workGroupNames, setWorkGroupNames] = useState<Map<string, string>>(new Map());

    const [positionMembershipBuffer, setPositionMembershipBuffer] = useState<Map<string, any>[]>([]);
    const [userGroupMembershipBuffer, setUserGroupMembershipBuffer] = useState<Map<string, any>[]>([]);
    const [workGroupMembershipBuffer, setWorkGroupMembershipBuffer] = useState<Map<string, any>[]>([]);

    const [showAddPosition, setShowAddPosition] = useState<boolean>(false);
    const [showAddUserGroup, setShowAddUserGroup] = useState<boolean>(false);
    const [showAddWorkGroup, setShowAddWorkGroup] = useState<boolean>(false);


    const [positionMembershipDeleteUSID, setPositionMembershipDeleteUSID] = useState<string | null>(null);
    const [userGroupMembershipDeleteUSID, setUserGroupMembershipDeleteUSID] = useState<string | null>(null);
    const [workGroupMembershipDeleteUSID, setWorkGroupMembershipDeleteUSID] = useState<string | null>(null);

    const [positionBackgroundOperation, setPositionBackgroundOperation] = useState<boolean>(false);
    const [userGroupBackgroundOperation, setUserGroupBackgroundOperation] = useState<boolean>(false);
    const [workGroupBackgroundOperation, setWorkGroupBackgroundOperation] = useState<boolean>(false);

    function loadStartedListener(ec: EnterpriseComponent) {
        if (ec.getName() === "PositionMembership") setPositionBackgroundOperation(true);
        if (ec.getName() === "UserGroupMembership") setUserGroupBackgroundOperation(true);
        if (ec.getName() === "WorkGroupMembership") setWorkGroupBackgroundOperation(true);
    }

    function loadCompletedListener(ec: EnterpriseComponent) {
        let b: Map<string, any>[] = prepareBuffer(ec);
        if (ec.getName() === "PositionMembership") { setPositionMembershipBuffer(b); setPositionBackgroundOperation(false); }
        if (ec.getName() === "UserGroupMembership") { setUserGroupMembershipBuffer(b); setUserGroupBackgroundOperation(false); }
        if (ec.getName() === "WorkGroupMembership") { setWorkGroupMembershipBuffer(b); setWorkGroupBackgroundOperation(false); }
    }

    function insertCompletedListener(ec: EnterpriseComponent) {
        let b: Map<string, any>[] = prepareBuffer(ec);
        if (ec.getName() === "PositionMembership") setPositionMembershipBuffer(b);
        if (ec.getName() === "UserGroupMembership") setUserGroupMembershipBuffer(b);
        if (ec.getName() === "WorkGroupMembership") setWorkGroupMembershipBuffer(b);
    }

    function deleteCompletedListener(ec: EnterpriseComponent) {
        let b: Map<string, any>[] = prepareBuffer(ec);
        if (ec.getName() === "PositionMembership") { setPositionMembershipDeleteUSID(null); setPositionMembershipBuffer(b); }
        if (ec.getName() === "UserGroupMembership") { setUserGroupMembershipDeleteUSID(null); setUserGroupMembershipBuffer(b); }
        if (ec.getName() === "WorkGroupMembership") { setWorkGroupMembershipDeleteUSID(null); setWorkGroupMembershipBuffer(b); }
    }

    function loadMembership(ec: EnterpriseComponent, idoc: string) {
        ec.addLoadStartedListener(loadStartedListener);
        ec.addLoadCompletedListener(loadCompletedListener);
        ec.addInsertCompletedListener(insertCompletedListener);
        ec.addDeleteCompletedListener(deleteCompletedListener);
        ec.newQuery();
        ec.setAttributeValue("SourceUSID", objectRevisionUSID);
        ec.setIDOCRequest(idoc);
        ec.setIDOCResponse(idoc);
        ec.executeQuery(1, 1000);
    }

    useEffect(() => {
        loadMembership(PositionMembershipEnterpriseComponent, "v1.PositionMembership-002");
        loadMembership(UserGroupMembershipEnterpriseComponent, "v1.UserGroupMembership-002");
        loadMembership(WorkGroupMembershipEnterpriseComponent, "v1.WorkGroupMembership-002");
        return () => {
            [PositionMembershipEnterpriseComponent, UserGroupMembershipEnterpriseComponent, WorkGroupMembershipEnterpriseComponent].forEach(ec => {
                ec.removeLoadStartedListener(loadStartedListener);
                ec.removeLoadCompletedListener(loadCompletedListener);
                ec.removeInsertCompletedListener(insertCompletedListener);
                ec.removeDeleteCompletedListener(deleteCompletedListener);
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PositionMembershipEnterpriseComponent, UserGroupMembershipEnterpriseComponent, WorkGroupMembershipEnterpriseComponent, objectRevisionUSID]);

    // The revision, for the Primary rows and the frozen test. Reloaded after a Primary edit saves, so
    // the row reflects what was just written rather than what was on screen before.
    useEffect(() => {
        function revisionLoaded(ec: EnterpriseComponent) {
            const rows: Map<string, any>[] = prepareBuffer(ec);
            setRevisionBuffer(rows.length > 0 ? rows[0] : null);
        }
        function revisionUpdated(ec: EnterpriseComponent) {
            revisionLoaded(ec);
            props.onPrimaryChanged?.();
        }

        ObjectRevisionEnterpriseComponent.addLoadCompletedListener(revisionLoaded);
        ObjectRevisionEnterpriseComponent.addUpdateCompletedListener(revisionUpdated);

        ObjectRevisionEnterpriseComponent.newQuery();
        ObjectRevisionEnterpriseComponent.setAttributeValue("USID", objectRevisionUSID);
        ObjectRevisionEnterpriseComponent.executeQuery(1, 1);

        return () => {
            ObjectRevisionEnterpriseComponent.removeLoadCompletedListener(revisionLoaded);
            ObjectRevisionEnterpriseComponent.removeUpdateCompletedListener(revisionUpdated);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ObjectRevisionEnterpriseComponent, objectRevisionUSID]);

    // Realm-wide Position / User Group / Work Group lists, once, into USID -> Name maps.
    useEffect(() => {
        function buildNames(ec: EnterpriseComponent, setNames: (m: Map<string, string>) => void) {
            const rows: Map<string, any>[] = prepareBuffer(ec);
            const map = new Map<string, string>();
            rows.forEach(row => map.set(row.get("USID"), row.get("Name")));
            setNames(map);
        }
        const positionListener = (ec: EnterpriseComponent) => buildNames(ec, setPositionNames);
        const userGroupListener = (ec: EnterpriseComponent) => buildNames(ec, setUserGroupNames);
        const workGroupListener = (ec: EnterpriseComponent) => buildNames(ec, setWorkGroupNames);

        PositionEnterpriseComponent.addLoadCompletedListener(positionListener);
        UserGroupEnterpriseComponent.addLoadCompletedListener(userGroupListener);
        WorkGroupEnterpriseComponent.addLoadCompletedListener(workGroupListener);

        [PositionEnterpriseComponent, UserGroupEnterpriseComponent, WorkGroupEnterpriseComponent].forEach(ec => {
            ec.newQuery();
            ec.executeQuery(1, 1000);
        });

        return () => {
            PositionEnterpriseComponent.removeLoadCompletedListener(positionListener);
            UserGroupEnterpriseComponent.removeLoadCompletedListener(userGroupListener);
            WorkGroupEnterpriseComponent.removeLoadCompletedListener(workGroupListener);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PositionEnterpriseComponent, UserGroupEnterpriseComponent, WorkGroupEnterpriseComponent]);

    function onCancelHandler() {
        setShowAddPosition(false);
        setShowAddUserGroup(false);
        setShowAddWorkGroup(false);
        setShowEditPrimaryPosition(false);
        setShowEditPrimaryUserGroup(false);
        setShowEditPrimaryWorkGroup(false);
    }

    useEffect(() => {
        const events = [
            "secondary-position-form-applet-cancel", "secondary-position-form-applet-save",
            "secondary-user-group-form-applet-cancel", "secondary-user-group-form-applet-save",
            "secondary-work-group-form-applet-cancel", "secondary-work-group-form-applet-save",
            "primary-position-form-applet-cancel", "primary-position-form-applet-save",
            "primary-user-group-form-applet-cancel", "primary-user-group-form-applet-save",
            "primary-work-group-form-applet-cancel", "primary-work-group-form-applet-save",
        ];
        events.forEach(e => EventBus.components.addListener(e, onCancelHandler));
        return () => { events.forEach(e => EventBus.components.removeListener(e, onCancelHandler)); };
    }, []);


    // Re-read whenever the primary changes -- including a change made through the pencil above, which
    // rewrites the object and so gives this a new USID to follow.
    useEffect(() => {
        const usid = revisionBuffer?.get("PrimaryPositionUSID");
        if (!usid) {
            setPrimaryPosition(null);
            return;
        }

        const listener = (ec: EnterpriseComponent) => {
            const rows: Map<string, any>[] = prepareBuffer(ec);
            setPrimaryPosition(rows.length > 0 ? rows[0] : null);
        };
        PrimaryPositionEnterpriseComponent.addLoadCompletedListener(listener);

        PrimaryPositionEnterpriseComponent.newQuery();
        PrimaryPositionEnterpriseComponent.setAttributeValue("USID", usid);
        PrimaryPositionEnterpriseComponent.setIDOCResponse("v1.Position-002");
        PrimaryPositionEnterpriseComponent.executeQuery(1, 1);

        return () => { PrimaryPositionEnterpriseComponent.removeLoadCompletedListener(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PrimaryPositionEnterpriseComponent, revisionBuffer?.get("PrimaryPositionUSID")]);

    // The same query for the other two owners. Kept as one effect per owner rather than a loop: each
    // watches its own USID, so changing the primary user group does not re-fetch the position.
    useEffect(() => {
        const usid = revisionBuffer?.get("PrimaryUserGroupUSID");
        if (!usid) {
            setPrimaryUserGroup(null);
            return;
        }

        const listener = (ec: EnterpriseComponent) => {
            const rows: Map<string, any>[] = prepareBuffer(ec);
            setPrimaryUserGroup(rows.length > 0 ? rows[0] : null);
        };
        PrimaryUserGroupEnterpriseComponent.addLoadCompletedListener(listener);

        PrimaryUserGroupEnterpriseComponent.newQuery();
        PrimaryUserGroupEnterpriseComponent.setAttributeValue("USID", usid);
        PrimaryUserGroupEnterpriseComponent.setIDOCResponse("v1.UserGroup-002");
        PrimaryUserGroupEnterpriseComponent.executeQuery(1, 1);

        return () => { PrimaryUserGroupEnterpriseComponent.removeLoadCompletedListener(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PrimaryUserGroupEnterpriseComponent, revisionBuffer?.get("PrimaryUserGroupUSID")]);

    useEffect(() => {
        const usid = revisionBuffer?.get("PrimaryWorkGroupUSID");
        if (!usid) {
            setPrimaryWorkGroup(null);
            return;
        }

        const listener = (ec: EnterpriseComponent) => {
            const rows: Map<string, any>[] = prepareBuffer(ec);
            setPrimaryWorkGroup(rows.length > 0 ? rows[0] : null);
        };
        PrimaryWorkGroupEnterpriseComponent.addLoadCompletedListener(listener);

        PrimaryWorkGroupEnterpriseComponent.newQuery();
        PrimaryWorkGroupEnterpriseComponent.setAttributeValue("USID", usid);
        PrimaryWorkGroupEnterpriseComponent.setIDOCResponse("v1.WorkGroup-002");
        PrimaryWorkGroupEnterpriseComponent.executeQuery(1, 1);

        return () => { PrimaryWorkGroupEnterpriseComponent.removeLoadCompletedListener(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [PrimaryWorkGroupEnterpriseComponent, revisionBuffer?.get("PrimaryWorkGroupUSID")]);
    function disassociate(ec: EnterpriseComponent, USID: string, setDeleteUSID: (u: string | null) => void) {
        ec.firstRecord();
        while (true) {
            if (ec.getAttributeValue("USID") === USID) break;
            if (!ec.nextRecord()) return;
        }
        setDeleteUSID(USID);
        ec.executeDelete();
    }

    function openAdd(setShow: (b: boolean) => void, ec: EnterpriseComponent, picklistAttribute: string) {
        // The Secondary applets read the share's SourceUSID from the session general-purpose variable,
        // which is how the membership EC learns which record it hangs off at insert time.
        sessionContext.setGeneralPurposeVariable(objectRevisionUSID);
        let attribute = ec.getAttribute(picklistAttribute);
        attribute?.getPicklist()!.setAllowNoSelection(false);
        setShow(true);
    }

    function stringAvatar(name: string) {
        return (name || "").split(" ").map(word => word[0]).join("").substring(0, 2);
    }

    // The Membership column: the account users of one position, drawn the same way wherever it
    // appears -- against the primary position and against each shared one.
    function renderMemberList(accountUsers: any[]) {
        return (
            <div style={{width: "300px", display: "flex", flexDirection: "column", marginTop: "5px"}}>
                {accountUsers.length === 0 ? (
                    <div style={{display: "flex", flexDirection: "row"}}>
                        <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Grey5, margin: "15px 0 8px 10px"}}>No members</Typography></div>
                    </div>
                ) : null}
                {accountUsers.map((accountUser: any) => (
                    <div key={accountUser.USID} style={{display: "flex", flexDirection: "row"}}>
                        <div style={{fontSize: 14, fontWeight: 500, color: Black, margin: "3px 0 0 10px"}}>
                            <Avatar sx={{bgcolor: Blue3, width: 25, height: 25, fontSize: "12px"}}>{stringAvatar(accountUser.Name)}</Avatar>
                        </div>
                        <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "5px 0 8px 10px"}}>{accountUser.Name}</Typography></div>
                    </div>
                ))}
            </div>
        );
    }

    // One owner type's section: the PRIMARY owner in a table of its own with its membership, then the
    // shared ones in a second table with theirs. Ported from the enlil-t8 Project Plan detail, which
    // does this for Positions and User Groups; Work Groups gets the same shape here rather than being
    // the odd one out.
    //
    // The two tables are drawn as one block -- the first rounded at the top, the second at the bottom,
    // and the first closing itself off when nothing is shared.
    function renderOwnerSection(owner: {
        title: string;                       // "Positions"
        singular: string;                    // "Position", for the column headers and the primary line
        addLabel: string;                    // "Add Shared Position"
        companyKey: string;                  // "Positions", for name resolution
        usidAttribute: string;               // "PrimaryPositionUSID" on the object
        nameAttribute: string;               // "PositionName" on a membership row
        primaryRecord: Map<string, any> | null;
        membershipEnterpriseComponent: EnterpriseComponent;
        membershipBuffer: Map<string, any>[];
        backgroundOperation: boolean;
        deleteUSID: string | null;
        setDeleteUSID: (u: string | null) => void;
        setShowAdd: (b: boolean) => void;
        picklistAttribute: string;           // "PositionUSID"
        onEditPrimary: () => void;
        offerCapabilities: boolean;          // Work Group shares carry no access level (decided 2026-09-16)
    }) {
        const primaryUSID = revisionBuffer?.get(owner.usidAttribute);
        const primaryOwnerName = primaryName(owner.companyKey, primaryUSID);
        // The person, read from the owner's own record rather than from the object. The object's
        // Stewardship attribute is the primary POSITION's name, not a person.
        const primaryAccountUser = owner.primaryRecord?.get("PrimaryAccountUserName") ?? null;
        const primaryMembers: any[] = owner.primaryRecord?.get("AccountUsers") ?? [];
        const hasShared = owner.membershipBuffer.length > 0;

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", backgroundColor: "#FFFFFF", marginTop: "30px", borderBottom: "1px solid #D8DDE4"}}>
                    <Typography style={{fontSize: 20, fontWeight: 500, color: "#000000"}}>{owner.title}</Typography>
                    {!frozen ? (
                        <Button className={css_buttons.blueIconButton} style={{marginBottom: "4px"}} disabled={owner.backgroundOperation}
                                onClick={() => openAdd(owner.setShowAdd, owner.membershipEnterpriseComponent, owner.picklistAttribute)}
                                startIcon={<ControlPoint sx={{color: Blue3, width: "20px", height: "20px"}}/>}>
                            <Typography style={{fontSize: 14, fontWeight: 500, color: "#000000"}}>{owner.addLabel}</Typography>
                        </Button>
                    ) : null}
                </div>

                <div style={{display: "flex", flexDirection: "row", alignItems: "center", backgroundColor: "#FFFFFF", marginTop: "12px"}}>
                    <Typography style={{fontSize: 14, fontWeight: 500, color: Black}}>Primary {owner.singular}:&nbsp;&nbsp;</Typography>
                    <Typography style={{fontSize: 14, fontWeight: 400, color: Grey5}}>
                        {primaryOwnerName ? `${primaryOwnerName}${primaryAccountUser ? ` (Primary Account User: ${primaryAccountUser})` : ""}` : "Not set"}
                    </Typography>
                    {mayEditPrimary(owner.primaryRecord?.get("PrimaryAccountUserUSID"),
                                    !!primaryUSID) ? (
                        <Tooltip title={`Change Primary ${owner.singular}`}>
                            <IconButton className={css_buttons.blueIconButton} style={{marginLeft: "5px", marginTop: "-2px"}} onClick={owner.onEditPrimary}>
                                <Edit sx={{color: Blue3, width: "20px", height: "20px"}}/>
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </div>

                <div style={{display: "flex", flexDirection: "column", backgroundColor: Grey3, border: "1px solid #7B8291", borderBottom: "0px",
                             borderTopLeftRadius: "5px", borderTopRightRadius: "5px",
                             borderBottomLeftRadius: hasShared ? "0px" : "5px", borderBottomRightRadius: hasShared ? "0px" : "5px", marginTop: "20px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "transparent", borderBottom: "1px solid #7B8291"}}>
                        <div style={{width: "410px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Primary {owner.singular}</Typography></div>
                        <div style={{width: "5px"}}><Typography style={{fontSize: 26, fontWeight: 500, color: Grey6, marginTop: "0px"}}>|</Typography></div>
                        <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>{owner.singular} Membership</Typography></div>
                    </div>
                    {primaryOwnerName ? (
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", backgroundColor: "#FFFFFF", borderBottom: hasShared ? "1px solid #EDEFF3" : "none"}}>
                            <div style={{display: "flex", flexDirection: "row"}}>
                                <div style={{marginTop: "10px", marginBottom: "10px", width: "410px"}}>
                                    <Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px", textOverflow: "ellipsis", overflow: "hidden"}}>{primaryOwnerName}</Typography>
                                </div>
                                {renderMemberList(primaryMembers)}
                            </div>
                        </div>
                    ) : (
                        <div style={{display: "flex", flexDirection: "row", backgroundColor: "#FFFFFF"}}>
                            <Typography style={{fontSize: 14, fontWeight: 500, color: Grey5, margin: "12px 0 12px 10px"}}>Not set</Typography>
                        </div>
                    )}
                    {/* A 5px strip of the container's own background, closing the table off.
                        Without it the last row -- which has square corners and a white background --
                        sits flush against the container's 5px rounded bottom, so the corners clip the
                        row and the block reads as cut off. Same device as the enlil-t8 original, and
                        the reason it is needed only when no second table follows. */}
                    {hasShared ? null : (
                        <div style={{display: "flex", flexDirection: "row", backgroundColor: "transparent", height: "5px"}}/>
                    )}
                </div>

                {hasShared ? (
                    <div style={{display: "flex", flexDirection: "column", backgroundColor: Grey3, border: "1px solid #7B8291",
                                 borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px", marginTop: "0px"}}>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "transparent", borderBottom: "1px solid #7B8291"}}>
                            <div style={{width: "410px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Secondary {owner.title}</Typography></div>
                            <div style={{width: "5px"}}><Typography style={{fontSize: 26, fontWeight: 500, color: Grey6, marginTop: "0px"}}>|</Typography></div>
                            <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>{owner.singular} Membership</Typography></div>
                        </div>
                        {owner.membershipBuffer.map(member => (
                            <div key={member.get("USID")} style={{display: "flex", flexDirection: "row", justifyContent: "space-between", backgroundColor: "#FFFFFF", borderBottom: "1px solid #EDEFF3"}}>
                                <div style={{display: "flex", flexDirection: "row"}}>
                                    <div style={{marginTop: "10px", marginBottom: "10px", width: "410px"}}>
                                        <Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px 10px 0 10px", textOverflow: "ellipsis", overflow: "hidden"}}>{member.get(owner.nameAttribute)}</Typography>
                                        {/* WHY IT IS THERE, when the platform put it there itself.
                                            Without this a share the approval engine wrote to let a
                                            board read what it must sign is indistinguishable from
                                            one somebody made deliberately -- and next to an empty
                                            Primary it reads as impossible. Ed, on SOP-00001: "no
                                            primary user group and two secondary user groups. This
                                            is impossible." It was not; the screen simply could not
                                            say so. The Detail tab has drawn this all along. */}
                                        {grantedBy(member) ? (
                                            <Typography style={{fontSize: 12, color: Grey5, margin: "0 10px 0 10px"}}>
                                                {grantedBy(member)}
                                            </Typography>
                                        ) : null}
                                    </div>
                                    {renderMemberList(member.get("AccountUsers") || [])}
                                </div>
                                {owner.offerCapabilities ? (
                                    <div style={{width: "240px", display: "flex", alignItems: "center", padding: "8px 10px"}}>
                                        <ShareAccessLevel membershipUSID={member.get("USID")} editable={!frozen}/>
                                    </div>
                                ) : null}
                                <div style={{width: "170px", display: "flex", flexDirection: "column"}}>
                                    <div style={{margin: "auto", display: "flex", flexDirection: "column", gap: "6px", padding: "8px 0"}}>
                                        {/* A FOLDER'S SHARE IS THE FOLDER'S: removed here it would come back at the folder's next
                                            change, so it is changed in the folder, under Folders, and not offered here. */}
                                        {frozen || member.get("GrantedByFolderUSID") ? null : member.get("USID") !== owner.deleteUSID ? (
                                            <Button style={{width: "150px"}} className={css_buttons.blueStandardButton}
                                                    onClick={() => disassociate(owner.membershipEnterpriseComponent, member.get("USID"), owner.setDeleteUSID)}>Disassociate</Button>
                                        ) : (
                                            <img style={{position: "relative"}} width="55" height="55" src={spinner} alt="spinner"/>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div style={{display: "flex", flexDirection: "row", backgroundColor: "transparent", height: "5px"}}/>
                    </div>
                ) : null}
            </>
        );
    }

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
            {/* NO GUIDE LINK HERE. Removed at Ed's request 2026-09-21. The guide is still shipped
                and still reachable from the Documentation page, which is where every guide is
                listed; a per-tab shortcut to one of them was the odd one out. guideLinks.test
                keeps the file honest either way -- it fails on a guide nothing links to, and the
                Documentation card is that link. */}
            {showAddPosition ? (<SecondaryPositionFormApplet enterpriseComponent={PositionMembershipEnterpriseComponent} defaultAction="New"/>) : null}
            {showAddUserGroup ? (<SecondaryUserGroupFormApplet enterpriseComponent={UserGroupMembershipEnterpriseComponent} defaultAction="New"/>) : null}
            {showAddWorkGroup ? (<SecondaryWorkGroupFormApplet enterpriseComponent={WorkGroupMembershipEnterpriseComponent} defaultAction="New"/>) : null}
            {showEditPrimaryPosition ? (<PrimaryPositionFormApplet enterpriseComponent={ObjectRevisionEnterpriseComponent} defaultAction="Edit"/>) : null}
            {showEditPrimaryUserGroup ? (<PrimaryUserGroupFormApplet enterpriseComponent={ObjectRevisionEnterpriseComponent} defaultAction="Edit"/>) : null}
            {showEditPrimaryWorkGroup ? (<PrimaryWorkGroupFormApplet enterpriseComponent={ObjectRevisionEnterpriseComponent} defaultAction="Edit"/>) : null}

            {renderOwnerSection({
                title: "Positions", singular: "Position", addLabel: "Add Shared Position",
                companyKey: "Positions", usidAttribute: "PrimaryPositionUSID", nameAttribute: "PositionName",
                primaryRecord: primaryPosition,
                membershipEnterpriseComponent: PositionMembershipEnterpriseComponent,
                membershipBuffer: positionMembershipBuffer,
                backgroundOperation: positionBackgroundOperation,
                deleteUSID: positionMembershipDeleteUSID, setDeleteUSID: setPositionMembershipDeleteUSID,
                setShowAdd: setShowAddPosition, picklistAttribute: "PositionUSID",
                onEditPrimary: () => setShowEditPrimaryPosition(true), offerCapabilities: true,
            })}

            {renderOwnerSection({
                title: "User Groups", singular: "User Group", addLabel: "Add Shared User Group",
                companyKey: "UserGroups", usidAttribute: "PrimaryUserGroupUSID", nameAttribute: "UserGroupName",
                primaryRecord: primaryUserGroup,
                membershipEnterpriseComponent: UserGroupMembershipEnterpriseComponent,
                membershipBuffer: userGroupMembershipBuffer,
                backgroundOperation: userGroupBackgroundOperation,
                deleteUSID: userGroupMembershipDeleteUSID, setDeleteUSID: setUserGroupMembershipDeleteUSID,
                setShowAdd: setShowAddUserGroup, picklistAttribute: "UserGroupUSID",
                onEditPrimary: () => setShowEditPrimaryUserGroup(true), offerCapabilities: true,
            })}

            {renderOwnerSection({
                title: "Work Groups", singular: "Work Group", addLabel: "Add Shared Work Group",
                companyKey: "WorkGroups", usidAttribute: "PrimaryWorkGroupUSID", nameAttribute: "WorkGroupName",
                primaryRecord: primaryWorkGroup,
                membershipEnterpriseComponent: WorkGroupMembershipEnterpriseComponent,
                membershipBuffer: workGroupMembershipBuffer,
                backgroundOperation: workGroupBackgroundOperation,
                deleteUSID: workGroupMembershipDeleteUSID, setDeleteUSID: setWorkGroupMembershipDeleteUSID,
                setShowAdd: setShowAddWorkGroup, picklistAttribute: "WorkGroupUSID",
                onEditPrimary: () => setShowEditPrimaryWorkGroup(true), offerCapabilities: false,
            })}

        </div>
    );
}
